import { useLocation } from 'react-router-dom';
import {
    useExportarPeriodicosMutation,
    useGenerarPdfConvocatoriaMutation,
    useGetEmployeesConvocatedQuery
} from '../../../services/periodicosApi';
import useGlobalFilters from '../../../hooks/useGlobalFilters';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export const useConvocados = () => {
    const location = useLocation();
    const { idConvocatoria, cuit, nombreOrganismoCentral } = location.state || {};
    const { enqueueSnackbar } = useSnackbar();
    const [exportarPeriodicos] = useExportarPeriodicosMutation();
    const { t } = useTranslation('periodicos');
    const [isExporting, setIsExporting] = useState(false);
    const [generarPdfConvocatorias] = useGenerarPdfConvocatoriaMutation();
    const [isDownloadingPdfConvocatoria, setIsDownloadingPdfConvocatoria] = useState(false);
    const {
        filters,
        setFilters,
        extraFilters,
        setExtraFilters,
        handleChangeExtraFilters,
        handleSubmitSearch,
        clearFilters
    } = useGlobalFilters();

    const handleExportarExcel = async () => {
        setIsExporting(true);
        try {
            const response = await exportarPeriodicos({ ...filters, idConvocatoria }).unwrap();

            const byteArray = new Uint8Array(response.data);
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Reporte-Convocados.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();

            enqueueSnackbar(t('alerts.exportExcel.generateSuccess'), { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('alerts.exportExcel.generateError'), {
                variant: 'error'
            });
        } finally {
            setIsExporting(false);
        }
    };

    const descargarPdfConvocatoria = async (idConvocatoria: number) => {
        setIsDownloadingPdfConvocatoria(true);

        const pdfResponse = await generarPdfConvocatorias(idConvocatoria).unwrap();

        const base64Data = pdfResponse?.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Detalle_convocatoria_${idConvocatoria}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setIsDownloadingPdfConvocatoria(false);
    };

    const { data, isFetching, refetch } = useGetEmployeesConvocatedQuery(
        { ...filters, idConvocatoria },
        { skip: !idConvocatoria }
    );

    return {
        convocatedData: data,
        isFetching,
        filters,
        setFilters,
        extraFilters,
        handleChangeExtraFilters,
        handleSubmitSearch,
        clearFilters,
        idConvocatoria,
        refetch,
        handleExportarExcel,
        isExporting,
        descargarPdfConvocatoria,
        isDownloadingPdfConvocatoria,
        nombreOrganismoCentral,
        cuit
    };
};
